import { useUpdateTasks } from 'features/tasks';
import React, { useMemo, useRef } from 'react';
import { NewTaskRow } from 'shared/components/connected/new-task-row';
import {
  RenderItemProps,
  rootId,
  TreeItem,
  TreeItemTypes,
  useTree,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { useToday } from 'shared/contexts/today';
import { useTaskDateShortcuts } from 'shared/hooks/use-task-date-shortcuts';
import { useTaskPriorityShortcuts } from 'shared/hooks/use-task-priority-shortcuts';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { lifeAreaOtherID } from 'shared/types/orderable-section';
import { Task } from 'shared/types/task';

import { useLifeAreaTree } from '../../hooks/use-life-area-tree';
import { GroupedListProps } from '../../types';
import { GoalHeader } from '../goal-header';
import { LifeAreaHeader } from '../life-area-header';
import { TreeTaskCard } from '../tree-task-card';
import * as Styled from './life-area-grouping-tasks-list.style';

const taskTreeItemTypes = [TreeItemTypes.Task];

const dropTypes = {
  [TreeItemTypes.Root]: [],
  [TreeItemTypes.LifeArea]: [TreeItemTypes.Task, TreeItemTypes.Goal],
  [TreeItemTypes.Goal]: [TreeItemTypes.Task],
  [TreeItemTypes.Task]: [],
  [TreeItemTypes.Priority]: [],
  [TreeItemTypes.Date]: [],
  [TreeItemTypes.DayPart]: [],
  [TreeItemTypes.Tag]: [],
  [TreeItemTypes.Type]: [],
  [TreeItemTypes.NewTask]: [],
  [TreeItemTypes.Section]: [],
};

export const LifeAreaGroupingTasksList: React.FC<GroupedListProps> = ({
  tasks,
  lifeAreas,
  goals,
  sortingMode,
  onReorder,
  customOrder = {},
  collapsedSections = [],
  onSectionCollapse,
  weekStartsOn,
  scrollElement,
  denseFormat,
  groupSettings,
  isContextMenuOpen,
  defaultNewTaskDate,
}) => {
  const today = useToday();

  const lifeAreasMap = useMemo(
    () =>
      lifeAreas.reduce<Record<ID, LifeArea>>((acc, lifeArea) => {
        acc[lifeArea.id] = lifeArea;
        return acc;
      }, {}),
    [lifeAreas],
  );
  const tasksMap = useMemo(
    () =>
      tasks.reduce<Record<ID, Task>>((acc, task) => {
        acc[task.id] = task;
        return acc;
      }, {}),
    [tasks],
  );

  const treeItems = useLifeAreaTree({
    lifeAreas,
    goals,
    tasks,
    customOrder,
    sortingMode,
    today,
    weekStartsOn,
    showEmptyGroups: groupSettings.showEmptyGroups,
  });

  const _onReorder = (updatedItems: TreeItem[]) => {
    if (!onReorder) {
      return;
    }

    const updatedDocs: Task[] = [];
    const updatedSections: { id: ID; taskSorting: ID[] }[] = [];
    updatedItems.forEach((item) => {
      if (item.type === TreeItemTypes.Task) {
        if (!tasksMap[item.id]) {
          return;
        }

        const taskToUpdate = structuredClone(tasksMap[item.id]);
        const newParentItem = updatedItems.find(({ children }) =>
          children?.includes(item.id),
        );
        const oldParentItem = Object.values(treeItems).find(({ children }) =>
          children?.includes(item.id),
        );

        // if sections are the same (or non-existing), no changes are needed for the task
        if (
          !newParentItem ||
          !oldParentItem ||
          newParentItem.id === oldParentItem?.id
        ) {
          return;
        }

        taskToUpdate.lifeAreaId = newParentItem?.id;
        taskToUpdate.goalId = null;
        updatedDocs.push(taskToUpdate);
      }
      if (
        item.type === TreeItemTypes.LifeArea &&
        (lifeAreasMap[item.id] || item.id === lifeAreaOtherID)
      ) {
        updatedSections.push({
          id: item.id,
          taskSorting: item.children
            ? item.children.filter((childId) => !childId.endsWith('-new-task'))
            : [],
        });
      }
    });

    onReorder(updatedSections, updatedDocs);
  };

  // using refs to prevent the renderItem from rerendering all items
  const lifeAreasMapRef = useRef(lifeAreasMap);
  lifeAreasMapRef.current = lifeAreasMap;

  const tasksMapRef = useRef(tasksMap);
  tasksMapRef.current = tasksMap;

  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;

  const goalsRef = useRef(goals);
  goalsRef.current = goals;

  const lifeAreasRef = useRef(lifeAreas);
  lifeAreasRef.current = lifeAreas;

  const renderItem: React.FC<RenderItemProps> = useMemo(
    () =>
      ({ item, itemInstance, tree, isMultiSelectModifierPressed }) => {
        const type = item.type;

        // Task
        if (type === TreeItemTypes.Task) {
          const currentTasksMap = tasksMapRef.current;
          const currentTasks = tasksRef.current;

          return currentTasksMap[item.sourceId] ? (
            <TreeTaskCard
              task={currentTasksMap[item.sourceId]}
              tree={tree}
              item={itemInstance}
              tasks={currentTasks}
              isMultiSelectModifierPressed={isMultiSelectModifierPressed}
              hideLifeArea
              displayDue={groupSettings.displayDue}
            />
          ) : null;
        }

        // New Task
        if (type === TreeItemTypes.NewTask) {
          const lifeAreaId = item.sourceId.replace('-new-task', '');
          const lifeArea =
            lifeAreaId === lifeAreaOtherID ? undefined : (lifeAreaId as ID);
          return (
            <NewTaskRow
              lifeArea={lifeArea}
              date={defaultNewTaskDate}
              startTime={null}
              duration={null}
              tags={groupSettings.tagId ? [groupSettings.tagId] : undefined}
            />
          );
        }

        // LifeArea
        if (type === TreeItemTypes.LifeArea) {
          const lifeArea: LifeArea | undefined =
            lifeAreasMapRef.current[item.sourceId];
          return (
            <LifeAreaHeader
              item={itemInstance}
              lifeArea={lifeArea}
              denseFormat={denseFormat}
            />
          );
        }

        // Goal
        if (type === TreeItemTypes.Goal) {
          return (
            <GoalHeader
              item={itemInstance}
              goals={goalsRef.current}
              lifeAreas={lifeAreasRef.current}
              denseFormat={denseFormat}
            />
          );
        }

        return null;
      },
    [
      denseFormat,
      defaultNewTaskDate,
      groupSettings?.displayDue,
      groupSettings?.tagId,
    ],
  );

  const [tree] = useTree();
  const { submit: updateTasks } = useUpdateTasks();

  useTaskPriorityShortcuts(tree, tasks, updateTasks);
  useTaskDateShortcuts(tree, tasks, updateTasks);

  return (
    <Styled.Container>
      <VirtualizedTreeList
        rootItemId={rootId}
        items={treeItems}
        renderItem={renderItem}
        scrollElement={scrollElement}
        multiselectTypes={taskTreeItemTypes}
        dragTypes={taskTreeItemTypes}
        onReorder={_onReorder}
        collapsedItems={collapsedSections}
        onCollapse={onSectionCollapse}
        denseFormat={denseFormat}
        dropTypes={dropTypes}
        isContextMenuOpen={isContextMenuOpen}
      />
    </Styled.Container>
  );
};
